/**
 * NotificationTemplateApiClient
 * 
 * Handles notification template management for developer console
 */

import { BaseApiClient } from './BaseApiClient';
import { 
  NotificationTemplate, 
  TemplateTestRequest, 
  TemplateTestResponse,
  NotificationEventType,
  NotificationChannel 
} from '../../types/notification';

export class NotificationTemplateApiClient extends BaseApiClient {
  /**
   * List all notification templates
   * @param filters Optional filters for templates
   * @returns Array of notification templates
   */
  async listTemplates(filters?: {
    eventType?: NotificationEventType;
    channel?: NotificationChannel;
    active?: boolean;
    appId?: string;
  }): Promise<{ templates: NotificationTemplate[] }> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.eventType) params.append('eventType', filters.eventType);
      if (filters.channel) params.append('channel', filters.channel);
      if (filters.active !== undefined) params.append('active', filters.active.toString());
      if (filters.appId) params.append('appId', filters.appId);
    }
    
    const queryString = params.toString();
    const url = `/admin/notification-templates${queryString ? `?${queryString}` : ''}`;
    return this.get<{ templates: NotificationTemplate[] }>(url);
  }

  /**
   * Get a single notification template
   * @param templateId Template ID
   * @returns The notification template
   */
  async getTemplate(templateId: string): Promise<NotificationTemplate> {
    return this.get<NotificationTemplate>(`/admin/notification-templates/${templateId}`);
  }

  /**
   * Create a new notification template
   * @param template Template data
   * @returns The created template
   */
  async createTemplate(template: Omit<NotificationTemplate, '_id' | 'createdAt' | 'updatedAt'>): Promise<NotificationTemplate> {
    return this.post<NotificationTemplate>('/admin/notification-templates', template);
  }

  /**
   * Update an existing notification template
   * @param templateId Template ID
   * @param updates Template updates
   * @returns The updated template
   */
  async updateTemplate(
    templateId: string, 
    updates: Partial<NotificationTemplate>
  ): Promise<NotificationTemplate> {
    return this.put<NotificationTemplate>(`/admin/notification-templates/${templateId}`, updates);
  }

  /**
   * Delete a notification template
   * @param templateId Template ID
   * @returns Success response
   */
  async deleteTemplate(templateId: string): Promise<{ success: boolean }> {
    return this.delete<{ success: boolean }>(`/admin/notification-templates/${templateId}`);
  }

  /**
   * Test a notification template
   * @param templateId Template ID
   * @param testData Test data including recipient and variables
   * @returns Test response with preview
   */
  async testTemplate(
    templateId: string, 
    testData: TemplateTestRequest
  ): Promise<TemplateTestResponse> {
    return this.post<TemplateTestResponse>(
      `/admin/notification-templates/${templateId}/test`, 
      testData
    );
  }

  /**
   * Activate or deactivate a template
   * @param templateId Template ID
   * @param active Whether to activate or deactivate
   * @returns Success response
   */
  async setTemplateActive(
    templateId: string, 
    active: boolean
  ): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(
      `/admin/notification-templates/${templateId}/activate`, 
      { active }
    );
  }

  /**
   * Get template analytics
   * @param templateId Template ID
   * @returns Analytics data
   */
  async getTemplateAnalytics(templateId: string): Promise<{
    usageCount: number;
    lastUsedAt?: Date;
    deliveryRate?: number;
    openRate?: number;
    clickRate?: number;
  }> {
    return this.get(`/admin/notification-templates/${templateId}/analytics`);
  }

  /**
   * Duplicate a template
   * @param templateId Template ID to duplicate
   * @param newName Name for the duplicated template
   * @returns The new template
   */
  async duplicateTemplate(
    templateId: string, 
    newName: string
  ): Promise<NotificationTemplate> {
    return this.post<NotificationTemplate>(
      `/admin/notification-templates/${templateId}/duplicate`, 
      { name: newName }
    );
  }

  /**
   * Get available template variables for an event type
   * @param eventType The event type
   * @returns Available variables
   */
  async getTemplateVariables(eventType: NotificationEventType): Promise<{
    variables: Array<{
      name: string;
      description: string;
      example: string;
      required?: boolean;
    }>;
  }> {
    return this.get(`/admin/notification-templates/variables/${eventType}`);
  }
}
